import React from 'react';

import { useLocalStorageGeolocation } from '@/hooks/useLocalStorageGeolocation';
import { googleMapsGeocode } from '@/libs/googleMaps/geocode';
import { arrayNotEmpty } from '@/utils/arrayNotEmpty';
import { getCityFromAddressComponents, getZipcodeFromAddressComponents } from '@/utils/googleMap';

const GeolocationApiContext = React.createContext();
export const GeolocationStateContext = React.createContext();
const GeolocationStateIsLoadingContext = React.createContext();
const GeolocationStateIsHydratedContext = React.createContext(true);

export const GeolocationProvider = ({ children }) => {
    const [state, setState] = useLocalStorageGeolocation();
    const [isHydrated, setIsHydrated] = React.useState(false);
    // Must stay declared after useLocalStorageGeolocation() so the storage read lands in the same batch.
    React.useEffect(() => {
        setIsHydrated(true);
    }, []);
    const currentSetStateRef = React.useRef(setState);
    React.useEffect(() => {
        currentSetStateRef.current = setState;
    }, [setState]);
    const currentStateRef = React.useRef(state);
    React.useEffect(() => {
        currentStateRef.current = state;
    }, [state]);

    const [geolocationIsLoading, setGeolocationIsLoading] = React.useState(false);

    const getZipCodeFromBrowserGeolocationPosition = React.useCallback(async () => {
        const searchParams = new URLSearchParams(window.location.search);
        const isDebugNavigatorGeolocation = searchParams.has('debug-navigator-geolocation');

        setGeolocationIsLoading(true);
        let currentPosition = null;

        if (!isDebugNavigatorGeolocation) {
            currentPosition = await new Promise((resolve) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        });
                    },
                    (...args) => {
                        console.error(...args);
                        resolve(null);
                    },
                );
            });
        } else {
            currentPosition = {
                lat: 41.0895249,
                lng: -73.8419063,
            };
        }

        if (!currentPosition) {
            console.error('Browser Geolocation not allowed');

            setGeolocationIsLoading(false);
            return null;
        }

        if (
            currentStateRef.current?.isBrowserGeolocation === true &&
            currentPosition.lat === currentStateRef.current?.lat &&
            currentPosition.lng === currentStateRef.current?.lng
        ) {
            return currentStateRef.current;
        }

        const response = await googleMapsGeocode({ location: currentPosition });

        setGeolocationIsLoading(false);

        if (!arrayNotEmpty(response?.results)) {
            return null;
        }

        return {
            zipCode: getZipcodeFromAddressComponents(response),
            city: getCityFromAddressComponents(response.results[0]),
            ...currentPosition,
            isBrowserGeolocation: true,
        };
    }, []);

    const getGeolocationPositionFromZipCode = React.useCallback(async (arg) => {
        if (
            currentStateRef.current?.isBrowserGeolocation === false &&
            (arg === currentStateRef.current?.zipCode || arg === currentStateRef.current?.city)
        ) {
            return {
                ...currentStateRef.current,
                recentSearch: arg,
            };
        }

        setGeolocationIsLoading(true);
        const response = await googleMapsGeocode({ address: arg });
        setGeolocationIsLoading(false);

        if (!arrayNotEmpty(response?.results)) {
            return null;
        }

        const currentPosition = {
            lat: response.results[0].geometry?.location?.lat(),
            lng: response.results[0].geometry?.location?.lng(),
        };

        // const isZipCode = !isNaN(arg);

        return {
            zipCode: !isNaN(arg) ? arg : getZipcodeFromAddressComponents(response),
            city: isNaN(arg) ? arg : getCityFromAddressComponents(response),
            ...currentPosition,
            isBrowserGeolocation: false,
            recentSearch: arg,
        };
    }, []);

    const setGeolocation = React.useCallback(
        async (arg) => {
            if (arg == null) {
                currentSetStateRef.current(null);
                return;
            }

            const { zipCode: zipCodeArg, city: cityArg, browserGeolocation = false } = arg;

            let newState = null;

            try {
                if (zipCodeArg) {
                    newState = await getGeolocationPositionFromZipCode(zipCodeArg);
                } else if (cityArg) {
                    newState = await getGeolocationPositionFromZipCode(cityArg);
                } else if (browserGeolocation) {
                    newState = await getZipCodeFromBrowserGeolocationPosition();
                }
            } catch (e) {
                console.error(e);
            }

            const storedState = currentStateRef.current;
            if (!newState && (storedState?.zipCode || storedState?.city)) {
                return storedState;
            }

            currentSetStateRef.current(newState);
            return newState;
        },
        [getGeolocationPositionFromZipCode, getZipCodeFromBrowserGeolocationPosition],
    );

    const api = React.useMemo(
        () => ({
            getZipCodeFromBrowserGeolocationPosition,
            getGeolocationPositionFromZipCode,
            setGeolocation,
        }),
        [
            getGeolocationPositionFromZipCode,
            getZipCodeFromBrowserGeolocationPosition,
            setGeolocation,
        ],
    );

    return (
        <GeolocationApiContext.Provider value={api}>
            <GeolocationStateIsLoadingContext.Provider value={geolocationIsLoading}>
                <GeolocationStateIsHydratedContext.Provider value={isHydrated}>
                    <GeolocationStateContext.Provider value={state}>
                        {children}
                    </GeolocationStateContext.Provider>
                </GeolocationStateIsHydratedContext.Provider>
            </GeolocationStateIsLoadingContext.Provider>
        </GeolocationApiContext.Provider>
    );
};

export const useGeolocationApi = () => React.useContext(GeolocationApiContext);
export const useGeolocationState = () => React.useContext(GeolocationStateContext);
export const useGeolocationStateIsLoading = () =>
    React.useContext(GeolocationStateIsLoadingContext);
export const useGeolocationStateIsHydrated = () =>
    React.useContext(GeolocationStateIsHydratedContext);

/**
 * Hook to manage geolocation state and updates.
 * This combines the functionalities of useGeolocationState and useGeolocationApi to more
 * efficiently manage geolocation data from a centralized point.
 *
 * @returns {Object} An object containing:
 * - `currentGeolocation` {Object|null} - The current geolocation state, which includes `zipCode` and `city`.
 * - `resolveGeolocation` {Function} - A function to resolve geolocation state. Accepts an object with optional `zipCode` and `city` properties. Returns current state if no parameters provided.
 * - `clearGeolocation` {Function} - A function to explicitly clear the geolocation state.
 * - `isLoading` {boolean} - A boolean indicating whether a geolocation update is in progress.
 * - `isHydrated` {boolean} - `false` until the stored location has been read after mount. When it
 *   turns `true`, `currentGeolocation` already holds the stored value (or `null` if none).
 *
 * @example
 * const { currentGeolocation, resolveGeolocation, clearGeolocation, isLoading } = useGeolocationManager();
 *
 * // Resolve geolocation with specific values
 * await resolveGeolocation({ zipCode: '12345' });
 * await resolveGeolocation({ city: 'New York, NY' });
 *
 * // Get current geolocation (fallback behavior)
 * const current = await resolveGeolocation();
 *
 * // Clear geolocation
 * await clearGeolocation();
 *
 * // Access current geolocation
 * console.log(currentGeolocation);
 */
export const useGeolocationManager = () => {
    const geolocationState = useGeolocationState();
    const { setGeolocation } = useGeolocationApi();
    const isHydrated = useGeolocationStateIsHydrated();
    const [isLoading, setIsLoading] = React.useState(false);

    // Use geolocationState as the single source of truth
    const currentGeolocation = geolocationState;

    const resolveGeolocation = React.useCallback(
        async ({ zipCode, city } = {}) => {
            // If no parameters provided, return current state (fallback behavior)
            if (!zipCode && !city) {
                return currentGeolocation;
            }

            // Return early if values haven't changed
            if (currentGeolocation?.zipCode === zipCode && currentGeolocation?.city === city) {
                return currentGeolocation;
            }

            setIsLoading(true);
            try {
                const newGeolocation = await setGeolocation({ zipCode, city });
                return newGeolocation;
            } finally {
                setIsLoading(false);
            }
        },
        [currentGeolocation, setGeolocation],
    );

    const clearGeolocation = React.useCallback(async () => {
        setIsLoading(true);
        try {
            await setGeolocation(null);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [setGeolocation]);

    return { currentGeolocation, resolveGeolocation, clearGeolocation, isLoading, isHydrated };
};

export const useHandleGeolocationButtonClick = (callback) => {
    const currentCallback = React.useRef(callback);
    React.useEffect(() => {
        currentCallback.current = callback;
    });

    const { setGeolocation } = useGeolocationApi();
    const state = useGeolocationState();
    const currentStateRef = React.useRef(state);
    React.useEffect(() => {
        currentStateRef.current = state;
    }, [state]);

    const handle = React.useCallback(
        async (e) => {
            e?.preventDefault?.();

            if (currentStateRef.current?.isBrowserGeolocation === true) {
                setGeolocation();
                currentCallback.current?.('');
                return;
            }

            const r = await setGeolocation({ browserGeolocation: true });

            if (r) {
                currentCallback.current?.(r.zipCode || r.city);
            } else {
                currentCallback.current?.('');
            }
        },
        [setGeolocation],
    );

    return handle;
};
